import { useCallback, useEffect, useState } from 'react';
import { MapPin, Navigation } from 'lucide-react';
import { apiUrl } from '../config/api';
import { apiFetch, isLoggedIn } from '../utils/session';
import Pagination, { DEFAULT_PAGE_SIZE, pagingParams } from '../components/Pagination';
import './AreaHeadSOS.css';

function openMaps(lat, lng) {
  const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${lat},${lng}`)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

export default function AreaHeadSOS() {
  const [alerts, setAlerts] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);

  const role = sessionStorage.getItem('sangramUserRole');

  const load = useCallback(async () => {
    if (!isLoggedIn() || (role !== 'Area Head' && role !== 'Admin')) {
      setError('Sign in as an Area Head or Admin to view SOS alerts.');
      setLoading(false);
      return;
    }
    setError('');
    try {
      const res = await apiFetch(apiUrl(`/sos/active?${pagingParams(page, pageSize)}`));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load alerts');
      setAlerts(data.alerts || []);
      setTotal(data.total ?? (data.alerts || []).length);
    } catch (e) {
      setError(e.message || 'Could not load alerts');
    } finally {
      setLoading(false);
    }
  }, [role, page, pageSize]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [load]);

  const respond = async (id) => {
    if (!isLoggedIn()) return;
    setBusyId(id);
    try {
      const res = await apiFetch(apiUrl(`/sos/${id}/respond`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not respond');
      await load();
    } catch (e) {
      setError(e.message || 'Respond failed');
    } finally {
      setBusyId(null);
    }
  };

  const resolve = async (id) => {
    if (!isLoggedIn()) return;
    setBusyId(id);
    try {
      const res = await apiFetch(apiUrl(`/sos/${id}/resolve`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not resolve');
      await load();
    } catch (e) {
      setError(e.message || 'Resolve failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="ahsos-page">
      <div className="ahsos-shell">
        <header className="ahsos-header">
          <div>
            <h1 className="ahsos-title">
              <MapPin size={26} strokeWidth={2.25} aria-hidden />
              Active SOS alerts
            </h1>
            <p className="ahsos-sub">
              {role === 'Admin'
                ? 'All open emergencies across zones. Respond means you are handling it; Resolve closes the alert.'
                : 'Emergencies in your assigned zone. Respond means you are on the way; you can Resolve anytime to close it.'}
            </p>
          </div>
        </header>

        {error ? (
          <p className="ahsos-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="ahsos-card">
          {loading ? (
            <p className="ahsos-muted">Loading…</p>
          ) : alerts.length === 0 ? (
            <p className="ahsos-muted">No active SOS alerts right now.</p>
          ) : (
            <ul className="ahsos-list">
              {alerts.map((a) => {
                const lat = a.location?.lat;
                const lng = a.location?.lng;
                const name = a.userId?.name || 'Unknown';
                const phone = a.userId?.phone || '';
                return (
                  <li key={a._id} className="ahsos-item">
                    <div className="ahsos-item-main">
                      <span className="ahsos-status" data-status={a.status}>
                        {a.status}
                      </span>
                      <strong className="ahsos-name">{name}</strong>
                      <span className="ahsos-meta">
                        {a.area}
                        {phone ? ` · ${phone}` : ''}
                      </span>
                      <span className="ahsos-coords">
                        {typeof lat === 'number' && typeof lng === 'number'
                          ? `${lat.toFixed(5)}, ${lng.toFixed(5)}`
                          : 'No coordinates'}
                      </span>
                    </div>
                    <div className="ahsos-item-actions">
                      <button
                        type="button"
                        className="ahsos-btn nav"
                        onClick={() => openMaps(lat, lng)}
                        disabled={typeof lat !== 'number' || typeof lng !== 'number'}
                      >
                        <Navigation size={18} aria-hidden />
                        Navigate
                      </button>
                      {a.status === 'Active' ? (
                        <button
                          type="button"
                          className="ahsos-btn primary"
                          disabled={busyId === a._id}
                          onClick={() => respond(a._id)}
                          title="Mark yourself as responding to this emergency"
                        >
                          {busyId === a._id ? '…' : "I'm responding"}
                        </button>
                      ) : null}
                      {a.status !== 'Resolved' ? (
                        <button
                          type="button"
                          className="ahsos-btn ghost"
                          disabled={busyId === a._id}
                          onClick={() => resolve(a._id)}
                          title="Mark this SOS as resolved"
                        >
                          Mark resolved
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {!loading ? (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
